// useViewDefinitionEditor.js

import { useEffect, useMemo } from 'react';

import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

import { getPluralLabel } from 'utilities/Languages';
import {
  normalizeEnumOptionsViewSortMode,
  resolveEnumOptionsForField,
} from 'utilities/EnumOptions';
import { getResolvedFields } from 'utilities/NodeType';
import { isRelationFieldType } from 'utilities/FieldTypes';
import { isBoardCardEligibleField } from 'views/board/utils/boardCardFields';

import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';
import { buildOrderedFieldOptions } from '../viewModalFieldOptions';
import { buildIncludeDescendantsLabel } from '../viewModalLabels';
import useBoardFieldSelection from './useBoardFieldSelection';
import useViewModalFields from './useViewModalFields';
import useViewModalState from './useViewModalState';
import useViewModalViewTypes from './useViewModalViewTypes';
import { normalizeViewGroups } from 'utilities/ViewGroups';

/**
 * Shared controller for ViewDefinitionEditor in modal and Studio surfaces.
 */
export default function useViewDefinitionEditor({
  draft,
  onDraftChange,
  typeKey,
  schemaFields = null,
  parentTypeKey = '',
  nodeKey = '',
  defaultTitlePrefix = '',
  initialViewType = 'LIST',
  isEditMode = false,
  isOpen = true,
  view = null,
  viewGroups: viewGroupsProp,
  width = '100%',
} = {}) {
  const language = useCurrentLanguage();
  const { getType, getAll, isReady } = useNodeTypes();
  const { theme } = useTheme();

  const isControlled = draft !== undefined && typeof onDraftChange === 'function';

  const modalState = useViewModalState({
    draft: isControlled ? draft : undefined,
    initialViewType,
    isEditMode,
    isOpen,
    onDraftChange: isControlled ? onDraftChange : undefined,
    view,
  });

  const {
    type,
    applyTypeSelection,
    boardField,
    setBoardField,
    boardLayout,
    setBoardLayout,
    boardEnumOptions,
    setBoardEnumOptions,
    viewGroupId,
    setViewGroupId,
    properties,
    setProperties,
    description,
    setDescription,
    title,
    setTitle,
    titleTouched,
    setTitleTouched,
    filters,
    setFilters,
    sorts,
    setSorts,
    includeDescendants,
    setIncludeDescendants,
    timelineField,
    setTimelineField,
    timelineRowMode,
    setTimelineRowMode,
    timelineGroupField,
    setTimelineGroupField,
    showUnscheduled,
    setShowUnscheduled,
    preset,
    setPreset,
    showHierarchy,
    setShowHierarchy,
    showDependencies,
    setShowDependencies,
    allowScheduleEdit,
    setAllowScheduleEdit,
    chartConfig,
    setChartConfig,
    scheduledType,
    setScheduledType,
    scheduledField,
    setScheduledField,
    slotIncrementMinutes,
    setSlotIncrementMinutes,
    scheduledFilters,
    setScheduledFilters,
    scheduledProperties,
    setScheduledProperties,
  } = modalState;

  const includeDescendantsLabel = useMemo(() => buildIncludeDescendantsLabel({
    nodeKey,
    typeKey,
    parentTypeKey,
    getType,
    language,
  }), [nodeKey, getType, typeKey, parentTypeKey, language]);

  const viewGroupOptions = useMemo(() => {
    const fromProp = normalizeViewGroups(viewGroupsProp);
    const fromType = normalizeViewGroups(getType?.(typeKey)?.viewGroups);
    const groups = fromProp.length ? fromProp : fromType;
    return [
      { value: '', label: 'Unsorted' },
      ...groups.map((group) => ({
        value: group.id,
        label: group.label,
      })),
    ];
  }, [getType, typeKey, viewGroupsProp]);

  const {
    propertyFields,
    filterableFields,
    filterFieldMap,
    sortFieldOptions,
    enumFields,
    hasEnumFields,
    timelineTemporalFields,
    timelineGroupFields,
  } = useViewModalFields({ typeKey, schemaFields });

  const {
    propertyFields: scheduledPropertyFields,
    filterableFields: scheduledFilterableFields,
    filterFieldMap: scheduledFilterFieldMap,
    sortFieldOptions: scheduledSortFieldOptions,
  } = useViewModalFields({ typeKey: scheduledType });

  const nodeTypesReady = isReady();

  const scheduledTypeOptions = useMemo(() => {
    if (!nodeTypesReady) return [];

    return (Array.isArray(getAll()) ? getAll() : [])
      .map((typeDef) => {
        const value = String(typeDef?.key || '').trim();
        if (!value) return null;

        const hasDateTimeRangeField = getResolvedFields(value, { includeHidden: true, excludeSecured: true })
          .some((field) => String(field?.type || '').trim().toLowerCase() === 'datetimerange');

        if (!hasDateTimeRangeField && value !== scheduledType) return null;

        return {
          value,
          label: String(typeDef?.label || value).trim() || value,
        };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' }));
  }, [getAll, nodeTypesReady, scheduledType]);

  const scheduledTemporalFields = useMemo(() => (
    getResolvedFields(scheduledType, { includeHidden: true, excludeSecured: true })
      .filter((field) => String(field?.type || '').trim().toLowerCase() === 'datetimerange')
  ), [scheduledType]);

  const scheduledTemporalFieldOptions = useMemo(() => (
    scheduledTemporalFields
      .map((field) => {
        const value = field?.name || field?.key || '';
        if (!value) return null;
        return {
          value,
          label: String(field?.label || value).trim() || value,
        };
      })
      .filter(Boolean)
  ), [scheduledTemporalFields]);

  const scheduledTemporalFieldMap = useMemo(() => {
    const map = new Map();
    scheduledTemporalFields.forEach((field) => {
      const fieldKey = field?.name || field?.key || '';
      if (fieldKey) map.set(fieldKey, field);
    });
    return map;
  }, [scheduledTemporalFields]);

  const { handleSelectViewType, selectedOptionKey, viewOptions } = useViewModalViewTypes({
    type,
    applyTypeSelection,
    preset,
    hasEnumFields,
  });

  const propertyFieldOptions = useMemo(() => {
    const sourceFields = Array.isArray(propertyFields)
      ? (type === 'BOARD'
        ? propertyFields.filter((field) => isBoardCardEligibleField(field))
        : propertyFields)
      : [];
    const base = sourceFields
      .map((field) => ({
        value: field?.name || field?.key || '',
        label: field?.label || field?.name || '',
      }))
      .filter((option) => option.value);
    const hasCover = base.some((option) => option?.value === 'cover');
    if (hasCover) return base;
    return [
      ...base,
      { value: 'cover', label: 'Cover' },
    ];
  }, [propertyFields, type]);

  const scheduledPropertyFieldOptions = useMemo(() => (
    Array.isArray(scheduledPropertyFields)
      ? scheduledPropertyFields
        .map((field) => ({
          value: field?.name || field?.key || '',
          label: field?.label || field?.name || '',
        }))
        .filter((option) => option.value)
      : []
  ), [scheduledPropertyFields]);

  const filterFieldOptions = useMemo(() => {
    const base = Array.isArray(sortFieldOptions) ? sortFieldOptions : [];
    return buildOrderedFieldOptions(base);
  }, [sortFieldOptions]);

  const scheduledFilterFieldOptions = useMemo(() => {
    const base = Array.isArray(scheduledSortFieldOptions) ? scheduledSortFieldOptions : [];
    return buildOrderedFieldOptions(base);
  }, [scheduledSortFieldOptions]);

  const chartDimensionOptions = useMemo(() => (
    (Array.isArray(filterableFields) ? filterableFields : [])
      .map((field) => {
        const value = field?.name || field?.key || '';
        if (!value) return null;
        return {
          value,
          label: field?.label || value,
          fieldType: String(field?.type || '').toLowerCase(),
        };
      })
      .filter(Boolean)
  ), [filterableFields]);

  const chartMeasureOptions = useMemo(() => (
    (Array.isArray(filterableFields) ? filterableFields : [])
      .filter((field) => {
        const normalizedType = String(field?.type || '').toLowerCase();
        return normalizedType === 'number'
          || normalizedType === 'money'
          || normalizedType === 'percentage';
      })
      .map((field) => {
        const value = field?.name || field?.key || '';
        if (!value) return null;
        return {
          value,
          label: field?.label || value,
        };
      })
      .filter(Boolean)
  ), [filterableFields]);

  const defaultTitle = useMemo(() => {
    if (!type) return '';
    const typeDef = typeKey ? getType(typeKey) : null;
    const pluralLabel = String(
      defaultTitlePrefix || (typeKey ? getPluralLabel(typeDef, typeKey, language) : 'Items'),
    ).trim() || 'Items';
    const viewLabel = viewOptions.find((option) => {
      const optionKey = String(option?.optionKey || option?.value || option?.key || '').trim();
      return optionKey === selectedOptionKey;
    })?.label || type;
    return `${pluralLabel} ${viewLabel}`.trim();
  }, [defaultTitlePrefix, language, selectedOptionKey, type, typeKey, getType, viewOptions]);

  const isGalleryDataType = useMemo(() => {
    if (type !== 'GALLERY') return false;
    if (!typeKey) return true;
    const def = typeKey ? getType(typeKey) : null;
    const key = String(def?.key || typeKey || '').toUpperCase();
    return key !== 'PHOTO' && key !== 'VIDEO';
  }, [type, typeKey, getType]);

  useBoardFieldSelection({
    type,
    hasEnumFields,
    enumFields,
    boardField,
    setBoardField,
  });

  const selectedBoardFieldDef = useMemo(() => (
    (enumFields || []).find((field) => {
      const fieldKey = field?.name || field?.key || '';
      return fieldKey && fieldKey === boardField;
    }) || null
  ), [enumFields, boardField]);

  const isNodelistBoardField = isRelationFieldType(selectedBoardFieldDef);
  const boardTargetType = String(selectedBoardFieldDef?.targetType || '').trim();

  const { options: boardRelationOptions } = useNodelistTargetOptions({
    targetType: boardTargetType,
    enabled: type === 'BOARD' && isNodelistBoardField && Boolean(boardTargetType),
  });

  const boardFieldOptions = useMemo(() => {
    if (!selectedBoardFieldDef) return [];

    if (isNodelistBoardField) {
      const orderIds = Array.isArray(boardEnumOptions?.optionOrderIds)
        ? boardEnumOptions.optionOrderIds.map((value) => String(value || '').trim()).filter(Boolean)
        : [];
      const byId = new Map(
        boardRelationOptions.map((option) => [
          option.value,
          {
            id: option.value,
            value: option.value,
            label: option.label,
          },
        ]),
      );
      const ordered = [];
      orderIds.forEach((id) => {
        if (!byId.has(id)) return;
        ordered.push(byId.get(id));
        byId.delete(id);
      });
      [...byId.values()]
        .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }))
        .forEach((option) => ordered.push(option));
      return ordered;
    }

    return resolveEnumOptionsForField(selectedBoardFieldDef, language, {
      sortModeOverride: normalizeEnumOptionsViewSortMode(boardEnumOptions?.sortMode),
      hiddenOptionIds: [],
      optionOrderIds: boardEnumOptions?.optionOrderIds,
    }).map((option) => ({
      id: String(option?.id || '').trim(),
      value: String(option?.value || '').trim(),
      label: String(option?.translations?.[language]?.label
        || option?.translations?.en?.label
        || option?.value
        || '').trim(),
    })).filter((option) => option.id && option.value);
  }, [
    selectedBoardFieldDef,
    isNodelistBoardField,
    boardRelationOptions,
    language,
    boardEnumOptions?.sortMode,
    boardEnumOptions?.optionOrderIds,
  ]);

  useEffect(() => {
    if (type !== 'BOARD' || !boardField) {
      setBoardEnumOptions((prev) => {
        const previous = prev && typeof prev === 'object' ? prev : {};
        const previousFieldKey = String(previous.fieldKey || '').trim();
        const previousOptionOrder = Array.isArray(previous.optionOrderIds)
          ? previous.optionOrderIds
            .map((value) => String(value || '').trim())
            .filter(Boolean)
          : [];
        const previousSortMode = normalizeEnumOptionsViewSortMode(previous.sortMode);
        const previousHiddenIds = Array.isArray(previous.hiddenOptionIds)
          ? previous.hiddenOptionIds
            .map((value) => String(value || '').trim())
            .filter(Boolean)
          : [];

        if (!previousFieldKey
          && previousOptionOrder.length === 0
          && previousSortMode === 'inherit'
          && previousHiddenIds.length === 0) {
          return previous;
        }

        return {
          fieldKey: '',
          optionOrderIds: [],
          sortMode: 'inherit',
          hiddenOptionIds: [],
        };
      });
      return;
    }

    const availableIds = new Set(boardFieldOptions.map((option) => option.id));

    setBoardEnumOptions((prev) => {
      const previous = prev && typeof prev === 'object' ? prev : {};
      const previousFieldKey = String(previous.fieldKey || '').trim();
      const explicitOrder = Array.isArray(previous.optionOrderIds)
        ? [...new Set(previous.optionOrderIds
          .map((value) => String(value || '').trim())
          .filter((id) => id && availableIds.has(id)))]
        : [];

      const hasLegacyOverrides = normalizeEnumOptionsViewSortMode(previous.sortMode) !== 'inherit'
        || (Array.isArray(previous.hiddenOptionIds) && previous.hiddenOptionIds.length > 0);

      const migratedOrder = previousFieldKey === boardField && !explicitOrder.length && hasLegacyOverrides
        ? boardFieldOptions.map((option) => option.id).filter((id) => availableIds.has(id))
        : [];

      const next = {
        fieldKey: boardField,
        optionOrderIds: previousFieldKey === boardField
          ? (explicitOrder.length ? explicitOrder : migratedOrder)
          : [],
        sortMode: 'inherit',
        hiddenOptionIds: [],
      };

      const previousHiddenIds = Array.isArray(previous.hiddenOptionIds)
        ? previous.hiddenOptionIds.map((value) => String(value || '').trim()).filter(Boolean)
        : [];
      const previousSortMode = normalizeEnumOptionsViewSortMode(previous.sortMode);

      if (
        previous.fieldKey === next.fieldKey
        && previousSortMode === next.sortMode
        && previousHiddenIds.length === 0
        && Array.isArray(previous.optionOrderIds)
        && previous.optionOrderIds.length === next.optionOrderIds.length
        && previous.optionOrderIds.every((value, index) => value === next.optionOrderIds[index])
      ) {
        return previous;
      }

      return next;
    });
  }, [type, boardField, boardFieldOptions, setBoardEnumOptions]);

  useEffect(() => {
    if (type !== 'TIMELINE') return;

    const hasValidTimelineField = (timelineTemporalFields || []).some((field) => {
      const fieldKey = field?.name || field?.key || '';
      return fieldKey && fieldKey === timelineField;
    });

    if (!hasValidTimelineField) {
      const scheduleField = (timelineTemporalFields || []).find((field) => {
        const fieldKey = String(field?.name || field?.key || '').trim();
        return fieldKey === 'schedule';
      });
      const preferredField = preset === 'gantt' && scheduleField
        ? scheduleField
        : timelineTemporalFields[0];
      const nextTimelineField = preferredField?.name || preferredField?.key || '';
      setTimelineField(nextTimelineField);
    }
  }, [preset, type, timelineTemporalFields, timelineField, setTimelineField]);

  useEffect(() => {
    if (type !== 'TIMELINE' || timelineRowMode !== 'grouped') {
      if (timelineGroupField) setTimelineGroupField('');
      return;
    }

    const hasValidGroupField = (timelineGroupFields || []).some((field) => {
      const fieldKey = field?.name || field?.key || '';
      return fieldKey && fieldKey === timelineGroupField;
    });

    if (!hasValidGroupField) {
      const firstGroupField = timelineGroupFields[0]?.name || timelineGroupFields[0]?.key || '';
      setTimelineGroupField(firstGroupField);
    }
  }, [
    type,
    timelineRowMode,
    timelineGroupFields,
    timelineGroupField,
    setTimelineGroupField,
  ]);

  useEffect(() => {
    if (type !== 'CHART') return;

    const dimensionValues = new Set(chartDimensionOptions.map((option) => option.value));
    const measureValues = new Set(chartMeasureOptions.map((option) => option.value));
    const aggregation = String(chartConfig?.aggregation || 'count').toLowerCase();
    const requiresMeasure = aggregation !== 'count';

    const nextXField = dimensionValues.has(chartConfig?.xField)
      ? chartConfig?.xField
      : (chartDimensionOptions[0]?.value || '');
    const nextGroupField = dimensionValues.has(chartConfig?.groupField)
      ? chartConfig?.groupField
      : '';
    const nextYField = requiresMeasure
      ? (measureValues.has(chartConfig?.yField)
        ? chartConfig?.yField
        : (chartMeasureOptions[0]?.value || ''))
      : '';

    if (
      nextXField !== chartConfig?.xField
      || nextGroupField !== chartConfig?.groupField
      || nextYField !== chartConfig?.yField
    ) {
      setChartConfig((prev) => ({
        ...(prev || {}),
        xField: nextXField,
        groupField: nextGroupField,
        yField: nextYField,
      }));
    }
  }, [
    type,
    chartConfig,
    chartDimensionOptions,
    chartMeasureOptions,
    setChartConfig,
  ]);

  useEffect(() => {
    if (type !== 'SCHEDULER') return;

    const hasValidScheduledType = scheduledTypeOptions.some((option) => option.value === scheduledType);
    if (!hasValidScheduledType) {
      const nextScheduledType = scheduledTypeOptions[0]?.value || '';
      if (nextScheduledType !== scheduledType) setScheduledType(nextScheduledType);
    }
  }, [type, scheduledType, scheduledTypeOptions, setScheduledType]);

  useEffect(() => {
    if (type !== 'SCHEDULER') return;

    const hasValidScheduledField = scheduledTemporalFieldOptions.some((option) => option.value === scheduledField);
    if (!hasValidScheduledField) {
      const nextScheduledField = scheduledTemporalFieldOptions[0]?.value || '';
      if (nextScheduledField !== scheduledField) setScheduledField(nextScheduledField);
    }
  }, [type, scheduledField, scheduledTemporalFieldOptions, setScheduledField]);

  useEffect(() => {
    if (!isOpen || isEditMode || titleTouched) return;
    if (!defaultTitle) return;
    if (title === defaultTitle) return;
    setTitle(defaultTitle);
  }, [defaultTitle, isEditMode, isOpen, setTitle, title, titleTouched]);

  const editorProps = {
    allowScheduleEdit,
    boardField,
    boardLayout,
    boardFieldOptions,
    chartConfig,
    chartDimensionOptions,
    chartMeasureOptions,
    description,
    enumFields,
    filterFieldMap,
    filterFieldOptions,
    filterableFields,
    filters,
    hasEnumFields,
    includeDescendants,
    includeDescendantsLabel,
    preset,
    properties,
    propertyFieldOptions,
    scheduledField,
    scheduledFieldOptions: scheduledTemporalFieldOptions,
    scheduledFilterFieldMap,
    scheduledFilterFieldOptions,
    scheduledFilterableFields,
    scheduledFilters,
    scheduledProperties,
    scheduledPropertyFieldOptions,
    scheduledType,
    scheduledTypeOptions,
    selectedOptionKey,
    setAllowScheduleEdit,
    setBoardEnumOptions,
    setBoardField,
    setBoardLayout,
    setChartConfig,
    setDescription,
    setFilters,
    setIncludeDescendants,
    setPreset,
    setProperties,
    setScheduledField,
    setScheduledFilters,
    setScheduledProperties,
    setScheduledType,
    setShowDependencies,
    setShowHierarchy,
    setShowUnscheduled,
    setSlotIncrementMinutes,
    setSorts,
    setTimelineField,
    setTimelineGroupField,
    setTimelineRowMode,
    setTitle,
    setTitleTouched,
    setType: handleSelectViewType,
    setViewGroupId,
    showDependencies,
    showHierarchy,
    showIncludeDescendants: Boolean(nodeKey && includeDescendantsLabel),
    showPropertiesSection: type === 'BOARD' || type === 'LIST' || type === 'ALPHA_LIST' || type === 'TABLE' || isGalleryDataType,
    showUnscheduled,
    showViewGroupField: viewGroupOptions.length > 1,
    slotIncrementMinutes,
    sorts,
    theme,
    timelineField,
    timelineGroupField,
    timelineGroupFields,
    timelineRowMode,
    timelineTemporalFields,
    title,
    type,
    viewGroupId,
    viewGroupOptions,
    viewOptions,
    width,
  };

  return {
    editorProps,
    filterFieldMap,
    isGalleryDataType,
    modalState,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
  };
}
